import { Client as StompClient } from '@stomp/stompjs';
import { logger } from 'common/services';
import EventEmitter from 'events';
import { updateKeycloakToken } from 'modules/keycloak';
import { keycloakService } from 'modules/keycloak/services';

/**
 * Socket Events
 */
export const SocketEvents = {
  connected: 'connected',
  disconnected: 'disconnected',
  reconnected: 'reconnected',
  message: 'message',
  error: 'error',
} as const;

/**
 * Socket Configuration
 */
export interface OpenNatterSocketConfig {
  wsUrl: string;
  reconnectDelay: number;
  heartbeatIncoming: number;
  heartbeatOutgoing: number;
  maxReconnectAttempts: number;
}

/**
 * Subscription handle
 */
interface Subscription {
  unsubscribe: () => void;
}

/**
 * Open Natter WebSocket Client
 * Handles dual-channel WebSocket communication for Open Natter events
 */
export class OpenNatterSocket extends EventEmitter {
  private readonly stompClient: StompClient;
  private readonly subscriptions = new Map<string, Subscription>();
  private isInitialized = false;
  private isReconnecting = false;
  private awaitingSubscriptions: string[] = [];
  private reconnectAttempts = 0;

  constructor(private readonly config: OpenNatterSocketConfig) {
    super();
    logger.debug('[OpenNatterSocket] Initializing socket', {
      wsUrl: config.wsUrl,
      reconnectDelay: config.reconnectDelay,
      maxReconnectAttempts: config.maxReconnectAttempts,
    });
    this.stompClient = this.createStompClient();
  }

  /**
   * Create and configure STOMP client
   */
  private createStompClient(): StompClient {
    return new StompClient({
      brokerURL: this.config.wsUrl,
      reconnectDelay: this.config.reconnectDelay,
      heartbeatIncoming: this.config.heartbeatIncoming,
      heartbeatOutgoing: this.config.heartbeatOutgoing,
      beforeConnect: async () => {
        // Track connection attempts (both initial and reconnections)
        this.reconnectAttempts++;

        const attemptType = this.isInitialized ? 'reconnection' : 'initial connection';
        logger.debug(`[OpenNatterSocket] Attempting ${attemptType}`, {
          attempt: this.reconnectAttempts,
          maxAttempts: this.config.maxReconnectAttempts,
          wsUrl: this.config.wsUrl,
          authenticated: keycloakService.authenticated,
        });

        // Check if max connection attempts reached
        if (this.reconnectAttempts > this.config.maxReconnectAttempts) {
          logger.error('[OpenNatterSocket] Max connection attempts reached', {
            attempts: this.reconnectAttempts,
            maxAttempts: this.config.maxReconnectAttempts,
            wsUrl: this.config.wsUrl,
          });
          this.emit(SocketEvents.error, new Error('Max connection attempts reached'));
          this.stompClient.deactivate();
          return;
        }

        if (this.isInitialized) {
          this.isReconnecting = true;
        }

        try {
          if (keycloakService.authenticated) {
            await updateKeycloakToken(keycloakService);
            logger.debug('[OpenNatterSocket] Token updated successfully');
          }

          if (keycloakService.token) {
            this.stompClient.connectHeaders = {
              Authorization: `Bearer ${keycloakService.token}`,
            };
            logger.debug('[OpenNatterSocket] Authorization header set', {
              tokenLength: keycloakService.token.length,
            });
          } else {
            logger.warn('[OpenNatterSocket] No authentication token available');
          }
        } catch (error) {
          logger.error('[OpenNatterSocket] Error updating token', {
            error,
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
          });
        }
      },
      onConnect: () => {
        const connectionType = this.isInitialized ? 'Reconnected' : 'Connected';
        logger.info(`[OpenNatterSocket] ${connectionType} successfully`, {
          attempt: this.reconnectAttempts,
          wsUrl: this.config.wsUrl,
          awaitingSubscriptions: this.awaitingSubscriptions.length,
        });

        if (this.isInitialized) {
          this.emit(SocketEvents.reconnected);
        } else {
          this.emit(SocketEvents.connected);
        }

        // Subscribe to awaiting channels
        for (const channel of this.awaitingSubscriptions) {
          this.subscribeToChannel(channel);
        }
        this.awaitingSubscriptions = [];

        this.isInitialized = true;
        this.isReconnecting = false;
        this.reconnectAttempts = 0; // Reset on successful connection
      },
      onDisconnect: () => {
        logger.info('[OpenNatterSocket] Disconnected', {
          wasInitialized: this.isInitialized,
          reconnectAttempts: this.reconnectAttempts,
          activeSubscriptions: this.subscriptions.size,
        });
        this.isInitialized = false;
        this.emit(SocketEvents.disconnected);
      },
      onWebSocketClose: (event: CloseEvent) => {
        logger.warn('[OpenNatterSocket] WebSocket closed', {
          code: event?.code,
          reason: event?.reason || 'No reason provided',
          wasClean: event?.wasClean,
          isInitialized: this.isInitialized,
          isReconnecting: this.isReconnecting,
          reconnectAttempts: this.reconnectAttempts,
          wsUrl: this.config.wsUrl,
        });

        if (!this.isInitialized || this.isReconnecting) {
          return;
        }

        logger.error('[OpenNatterSocket] Connection closed unexpectedly', {
          activeSubscriptions: this.subscriptions.size,
        });

        // Move current subscriptions to awaiting for reconnect
        this.awaitingSubscriptions = Array.from(this.subscriptions.keys());
        this.subscriptions.clear();
      },
      onStompError: (frame) => {
        logger.error('[OpenNatterSocket] STOMP protocol error', {
          command: frame.command,
          headers: frame.headers,
          body: frame.body,
          reconnectAttempts: this.reconnectAttempts,
          wsUrl: this.config.wsUrl,
        });
        this.emit(SocketEvents.error, new Error(`STOMP protocol error: ${frame.command}`));
      },
      onWebSocketError: (event: Event) => {
        const errorDetails = {
          type: event?.type,
          message: (event as ErrorEvent)?.message || 'WebSocket connection error',
          reconnectAttempts: this.reconnectAttempts,
          wsUrl: this.config.wsUrl,
          readyState: this.stompClient.webSocket?.readyState,
          readyStateDescription: this.getReadyStateDescription(this.stompClient.webSocket?.readyState),
        };

        logger.error('[OpenNatterSocket] WebSocket connection error', errorDetails);
        this.emit(SocketEvents.error, event);
      },
      debug: (message) => {
        logger.debug('[OpenNatterSocket] STOMP debug', { message });
      },
    });
  }

  /**
   * Get human-readable description of WebSocket ready state
   */
  private getReadyStateDescription(readyState: number | undefined): string {
    if (readyState === undefined) return 'UNKNOWN';
    switch (readyState) {
      case 0:
        return 'CONNECTING';
      case 1:
        return 'OPEN';
      case 2:
        return 'CLOSING';
      case 3:
        return 'CLOSED';
      default:
        return `UNKNOWN (${readyState})`;
    }
  }

  /**
   * Subscribe to a channel
   */
  private subscribeToChannel(destination: string): void {
    if (!this.stompClient.connected) {
      if (!this.awaitingSubscriptions.includes(destination)) {
        this.awaitingSubscriptions.push(destination);
      }
      return;
    }

    const subscription = this.stompClient.subscribe(destination, (message) => {
      try {
        const payload = JSON.parse(message.body);
        this.emit(SocketEvents.message, { destination, payload });
      } catch (error) {
        logger.error('[OpenNatterSocket] Failed to parse message', {
          error,
          destination,
          messageBody: message.body?.substring(0, 200), // Log first 200 chars
          messageHeaders: message.headers,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    });

    this.subscriptions.set(destination, subscription);
    logger.debug('[OpenNatterSocket] Subscribed to channel', { destination });
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    if (!this.stompClient.active) {
      logger.debug('[OpenNatterSocket] Activating connection', {
        wsUrl: this.config.wsUrl,
        reconnectDelay: this.config.reconnectDelay,
      });
      this.stompClient.activate();
    } else {
      logger.debug('[OpenNatterSocket] Connection already active, ignoring connect request');
    }
  }

  /**
   * Disconnect from the WebSocket server
   * Resets connection state to allow clean reconnection
   */
  disconnect(): void {
    if (this.stompClient.active) {
      logger.debug('[OpenNatterSocket] Deactivating connection', {
        reconnectAttempts: this.reconnectAttempts,
        activeSubscriptions: this.subscriptions.size,
      });
      this.stompClient.deactivate();
    } else {
      logger.debug('[OpenNatterSocket] Connection already inactive, ignoring disconnect request');
    }

    // Reset connection attempt counter to allow fresh reconnection
    // This prevents users from being blocked after manual disconnect + reconnect
    this.reconnectAttempts = 0;

    // Clean up EventEmitter listeners to prevent memory leaks
    this.removeAllListeners();
  }

  /**
   * Subscribe to personal and broadcast channels for an Open Natter event
   */
  subscribe(channels: { personal: string; broadcast: string }): void {
    logger.debug('[OpenNatterSocket] Subscribing to channels', {
      personal: channels.personal,
      broadcast: channels.broadcast,
      isConnected: this.stompClient.connected,
    });
    this.subscribeToChannel(channels.personal);
    this.subscribeToChannel(channels.broadcast);
  }

  /**
   * Unsubscribe from personal and broadcast channels
   */
  unsubscribe(channels: { personal: string; broadcast: string }): void {
    logger.debug('[OpenNatterSocket] Unsubscribing from channels', {
      personal: channels.personal,
      broadcast: channels.broadcast,
    });

    [channels.personal, channels.broadcast].forEach((destination) => {
      const subscription = this.subscriptions.get(destination);
      if (subscription) {
        subscription.unsubscribe();
        this.subscriptions.delete(destination);
        logger.debug('[OpenNatterSocket] Unsubscribed from channel', { destination });
      }

      // Remove from awaiting subscriptions if present
      this.awaitingSubscriptions = this.awaitingSubscriptions.filter((d) => d !== destination);
    });
  }

  /**
   * Check if the socket is connected
   */
  isConnected(): boolean {
    return this.stompClient.connected;
  }
}

// Socket instance is managed by OpenNatterFeature for proper lifecycle management
// Import from '../OpenNatterFeature' to get the socket instance
